// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useOctoRuleQuery } from "@/api/hooks/use-octo-rules";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OctoRuleActivityTab } from "@/features/octo/octo-rule-activity-tab";
import { OctoRuleInfoTab } from "@/features/octo/octo-rule-info-tab";
import { useDebugMode } from "@/lib/debug-mode";

export const Route = createFileRoute("/_authenticated/octo/rules/$ruleId/")({
  // The Edit tab moved to `/octo/rules/$ruleId/edit`. Old links and bookmarks carrying `?t=edit`
  // are forwarded rather than silently dropped to Info.
  beforeLoad: ({ params, search }) => {
    if (search.t === "edit") throw redirect({ to: "/octo/rules/$ruleId/edit", params, search: {} });
  },
  component: OctoRuleDetailPage,
});

function OctoRuleDetailPage() {
  const { ruleId } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { debugMode } = useDebugMode();
  const octoRuleId = Number(ruleId);
  // Already loaded by the layout, which only renders this page once the rule is in hand.
  const { data: rule } = useOctoRuleQuery(octoRuleId);

  const tabAllowed: Record<string, boolean> = {
    info: true,
    // Activity log is work-in-progress (mocked) — debug-mode only.
    activity: debugMode,
  };
  const requestedTab = tab ?? "info";
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  function handleTabChange(newTab: string) {
    navigate({
      to: "/octo/rules/$ruleId",
      params: { ruleId },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  if (!rule) return null;

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
      <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
        <TabsList>
          <TabsIndicator />
          <TabsTrigger value="info">{t("octoRules.detail.tabs.info")}</TabsTrigger>
          {debugMode && <TabsTrigger value="activity">{t("octoRules.detail.tabs.activity")}</TabsTrigger>}
        </TabsList>
      </div>
      <TabsContent value="info" className="space-y-4">
        <OctoRuleInfoTab rule={rule} />
      </TabsContent>
      {debugMode && (
        <TabsContent value="activity" className="space-y-4">
          <OctoRuleActivityTab octoRuleId={octoRuleId} initialFilters={activeTab === "activity" ? f : undefined} />
        </TabsContent>
      )}
    </Tabs>
  );
}
